import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const PROJECT_IMAGES = [
  // Czudar Péter Szobafestő
  "/images/project-1-czudar.png",
  // Black Car Service
  "/images/project-2-blackcar.png",
  // Gazsó-Pádár Papír-írószer
  "/images/project-3-gazsopadar.png",
];

const numbers = ["01", "02", "03"];

const Portfolio = () => {
  const { t, lang } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(null);

  // Close on ESC + lock body scroll while open
  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIndex(null);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [activeIndex]);

  const activeProject =
    activeIndex !== null ? t.portfolio.items[activeIndex] : null;

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="max-w-3xl mb-16 md:mb-20"
        >
          <span className="inline-block text-xs md:text-sm font-medium uppercase tracking-[0.25em] text-[#3B82F6] mb-6">
            {t.portfolio.label}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1B2A4A] leading-[1.1] tracking-tight">
            {t.portfolio.headingStart}{" "}
            <span className="italic font-medium">
              {t.portfolio.headingItalic}
            </span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {t.portfolio.items.map((p, i) => (
            <motion.div
              key={i}
              data-testid={`portfolio-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.7,
                delay: (i % 3) * 0.1,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="group"
            >
              <button
                type="button"
                data-testid={`portfolio-card-trigger-${i}`}
                onClick={() => setActiveIndex(i)}
                aria-label={`${p.name} — ${lang === "hu" ? "nagy nézet" : "view larger"}`}
                className="relative block w-full aspect-[4/5] overflow-hidden rounded-2xl bg-[#F1F5F9] border border-[#1B2A4A]/5 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
              >
                <img
                  src={PROJECT_IMAGES[i]}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4A]/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                  <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/90 bg-[#1B2A4A]/70 backdrop-blur-md px-2.5 py-1 rounded-full">
                    {t.portfolio.projectTag} {numbers[i]}
                  </span>
                  <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/0 group-hover:text-white/90 transition-colors duration-500 bg-[#1B2A4A]/0 group-hover:bg-[#1B2A4A]/70 backdrop-blur-md px-2.5 py-1 rounded-full">
                    {lang === "hu" ? "Nagyítás" : "Zoom"} +
                  </span>
                </div>
              </button>

              <div className="pt-6 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-[#1B2A4A] leading-snug">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-[#475569] font-light leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {activeIndex !== null && (
          <motion.div
            key="lightbox"
            data-testid="portfolio-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setActiveIndex(null)}
            className="fixed inset-0 z-[100] bg-[#0F172A]/90 backdrop-blur-md flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          >
            <button
              type="button"
              data-testid="portfolio-lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                setActiveIndex(null);
              }}
              aria-label={lang === "hu" ? "Bezárás" : "Close"}
              className="absolute top-6 right-6 md:top-8 md:right-8 inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/15 text-white transition-all duration-300 active:scale-95"
            >
              <X strokeWidth={1.5} className="w-5 h-5" />
            </button>

            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-6xl w-full max-h-[88vh] flex flex-col items-center cursor-default"
            >
              <img
                src={PROJECT_IMAGES[activeIndex]}
                alt={activeProject?.name}
                className="max-w-full max-h-[78vh] object-contain rounded-xl shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)]"
              />
              {activeProject && (
                <div className="mt-5 md:mt-6 text-center max-w-2xl px-2">
                  <p className="font-serif text-xl md:text-2xl font-semibold text-white">
                    {activeProject.name}
                  </p>
                  <p className="mt-1.5 text-sm md:text-base text-white/65 font-light leading-relaxed">
                    {activeProject.desc}
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
